/**
 * 관리자 프롬프트 템플릿 (M14 / Phase 1) React Query 훅.
 *
 * - 조회:
 *     ``usePrompts({ name, status })``  — 60초 staleTime (리스트).
 *     ``useActivePrompt(name)``         — 404 를 정상 응답으로 처리 (코드 default 사용 중).
 *     ``usePromptHistory(name)``        — 히스토리 다이얼로그용.
 *     ``usePrompt(id)``                 — 편집 모달 prefetch 용.
 * - 변이 6종: 생성 / 수정 / 삭제 / 활성화 / 롤백 / 테스트.
 *   변이 성공 시 list / active / history 캐시를 일괄 invalidate.
 *
 * 캐시 키 규약:
 *   ["admin", "prompts", "list", { name, status }]
 *   ["admin", "prompts", "active", { name }]
 *   ["admin", "prompts", "history", name]
 *   ["admin", "prompts", "detail", id]
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "@/lib/api/client";
import {
  activatePrompt,
  createPromptDraft,
  deletePromptDraft,
  DEFAULT_PROMPT_NAME,
  getActivePrompt,
  getPrompt,
  getPromptHistory,
  listPrompts,
  rollbackPrompt,
  testPrompt,
  updatePromptDraft,
  type ListPromptsParams,
  type PromptTemplateActivatePayload,
  type PromptTemplateCreatePayload,
  type PromptTemplateOut,
  type PromptTemplateTestRequest,
  type PromptTemplateTestResponse,
  type PromptTemplateUpdatePayload,
} from "@/lib/api/prompts";

// ────────────── 캐시 키 ──────────────

export const PROMPTS_QUERY_KEY = ["admin", "prompts"] as const;

export const promptsKeys = {
  all: PROMPTS_QUERY_KEY,
  lists: () => [...PROMPTS_QUERY_KEY, "list"] as const,
  list: (params: ListPromptsParams = {}) =>
    [
      ...PROMPTS_QUERY_KEY,
      "list",
      { name: params.name ?? null, status: params.status ?? null },
    ] as const,
  active: (name: string) => [...PROMPTS_QUERY_KEY, "active", { name }] as const,
  history: (name: string) => [...PROMPTS_QUERY_KEY, "history", name] as const,
  details: () => [...PROMPTS_QUERY_KEY, "detail"] as const,
  detail: (id: number) => [...PROMPTS_QUERY_KEY, "detail", id] as const,
};

// ────────────── 조회 ──────────────

export interface UsePromptsOptions extends ListPromptsParams {
  enabled?: boolean;
}

export function usePrompts(options: UsePromptsOptions = {}) {
  const { enabled = true, ...params } = options;
  return useQuery<PromptTemplateOut[]>({
    queryKey: promptsKeys.list(params),
    queryFn: () => listPrompts(params),
    staleTime: 60_000,
    enabled,
  });
}

/**
 * 현재 active 단일 행. **백엔드는 active 가 없으면 404 를 응답**하므로,
 * 호출측은 ``isError`` + ``error.status === 404`` 를 "코드 default 사용 중" 으로 표시.
 *
 * 404 자체는 retry 하지 않도록 한 번만 호출.
 */
export function useActivePrompt(name: string = DEFAULT_PROMPT_NAME) {
  return useQuery<PromptTemplateOut, ApiError>({
    queryKey: promptsKeys.active(name),
    queryFn: () => getActivePrompt(name),
    retry: (failureCount, error) => {
      if (error instanceof ApiError && error.status === 404) return false;
      return failureCount < 1;
    },
    staleTime: 60_000,
  });
}

export function usePromptHistory(name: string | null | undefined) {
  return useQuery<PromptTemplateOut[]>({
    queryKey: promptsKeys.history(name ?? ""),
    queryFn: () => {
      if (!name) {
        throw new Error("prompt name is required");
      }
      return getPromptHistory(name);
    },
    enabled: Boolean(name),
    staleTime: 30_000,
  });
}

export function usePrompt(id: number | null | undefined) {
  return useQuery<PromptTemplateOut>({
    queryKey: promptsKeys.detail(id ?? -1),
    queryFn: () => {
      if (!id) {
        throw new Error("prompt id is required");
      }
      return getPrompt(id);
    },
    enabled: Boolean(id),
    staleTime: 60_000,
  });
}

// ────────────── 변이 ──────────────

function invalidatePromptsAll(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: promptsKeys.all });
}

export function useCreatePromptDraft() {
  const qc = useQueryClient();
  return useMutation<PromptTemplateOut, Error, PromptTemplateCreatePayload>({
    mutationFn: createPromptDraft,
    onSuccess: (created) => {
      qc.setQueryData(promptsKeys.detail(created.id), created);
      invalidatePromptsAll(qc);
    },
  });
}

interface UpdatePromptVars {
  id: number;
  payload: PromptTemplateUpdatePayload;
}

export function useUpdatePromptDraft() {
  const qc = useQueryClient();
  return useMutation<PromptTemplateOut, Error, UpdatePromptVars>({
    mutationFn: ({ id, payload }) => updatePromptDraft(id, payload),
    onSuccess: (updated) => {
      qc.setQueryData(promptsKeys.detail(updated.id), updated);
      invalidatePromptsAll(qc);
    },
  });
}

export function useDeletePromptDraft() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePromptDraft(id),
    onSuccess: (_data, id) => {
      void qc.invalidateQueries({ queryKey: promptsKeys.detail(id) });
      invalidatePromptsAll(qc);
    },
  });
}

interface ActivatePromptVars {
  id: number;
  payload?: PromptTemplateActivatePayload;
}

export function useActivatePrompt() {
  const qc = useQueryClient();
  return useMutation<PromptTemplateOut, Error, ActivatePromptVars>({
    mutationFn: ({ id, payload = {} }) => activatePrompt(id, payload),
    onSuccess: (activated) => {
      qc.setQueryData(promptsKeys.detail(activated.id), activated);
      // active 와 history 가 한 번에 영향받음 — 전부 invalidate.
      invalidatePromptsAll(qc);
    },
  });
}

interface RollbackPromptVars {
  archivedId: number;
  payload?: PromptTemplateActivatePayload;
}

export function useRollbackPrompt() {
  const qc = useQueryClient();
  return useMutation<PromptTemplateOut, Error, RollbackPromptVars>({
    mutationFn: ({ archivedId, payload = {} }) => rollbackPrompt(archivedId, payload),
    onSuccess: (clone) => {
      qc.setQueryData(promptsKeys.detail(clone.id), clone);
      invalidatePromptsAll(qc);
    },
  });
}

/**
 * Dry-run mutation.
 *
 * `useMutation` 을 사용하는 이유: 캐싱하면 안 됨 (입력이 매번 달라짐) +
 * 사용자가 명시적으로 "실행" 버튼을 누를 때만 트리거되어야 함.
 */
export function usePromptTest() {
  return useMutation<PromptTemplateTestResponse, Error, PromptTemplateTestRequest>({
    mutationFn: testPrompt,
  });
}
